import { useQuery } from '@apollo/client/react';

import COUNT_DIDS_FOR_SWITCH from '../gql/countDidsForSwitch.gql';
import GET_DIDS_FOR_SWITCH from '@/features/dids/gql/getDidsForSwitch.gql';

/**
 * How many numbers use a switch, so pages can warn that forwarding changes
 * affect all of them. Tries an exact count first (`countDids` filtered by
 * switch_id); if the backend rejects that, falls back to checking whether more
 * than one number is attached, and `count` stays null.
 */
export const useSwitchNumberCount = (switchId: string | null | undefined) => {
  const exact = useQuery(COUNT_DIDS_FOR_SWITCH, {
    variables: { queryList: [{ key: 'switch_id', value: { value: switchId } }] },
    skip: !switchId,
  });

  const countFailed = !!exact.error;
  const fallback = useQuery(GET_DIDS_FOR_SWITCH, {
    variables: { id: switchId ?? undefined, first: 2 },
    skip: !switchId || !countFailed,
  });

  const count = countFailed ? null : (exact.data?.countDids?.count ?? null);
  const isShared =
    count !== null ? count > 1 : (fallback.data?.didsForSwitch?.edges.length ?? 0) > 1;

  return {
    /** Exact number of numbers on the switch, or null when only "shared or not" is known */
    count,
    /** More than one number uses the switch */
    isShared,
    loading: exact.loading || fallback.loading,
  };
};
